import { format } from 'date-fns';

/**
 * A show's own timezone and date-range display helpers.
 *
 * The actual timezone-resolution logic (STATE_TIMEZONES, timezoneForLocation,
 * timezoneForContact) moved to shared/showTimezone.js, task: 2026-09-25, so
 * worker/lib/googlesheets.ts can use the exact same logic rather than a
 * second, hand-duplicated copy. Re-exported here so every existing import of
 * '@/lib/showTimezone' keeps working unchanged. See that file's own header
 * for the full reasoning.
 */
export { DEFAULT_TIMEZONE, timezoneForLocation, timezoneForContact } from '@shared/showTimezone.js';

/**
 * A show's own start-to-end date range, "Wed Sep 23 to Fri Sep 25, 2026".
 *
 * Extracted here, task: full date-display sweep, 2026-09-25, after the same
 * exact range logic, duplicated in HomeHeader.jsx and Shows.jsx, got the
 * day-of-week fix in only one of the two places on the first pass. One
 * function now, imported by both, so that specific failure mode (fixed in
 * one spot, missed in its copy) cannot happen again.
 *
 * Kept in this file, not shared/showTimezone.js: it needs date-fns, a
 * frontend-only dependency with no reason to exist in the Worker bundle.
 *
 * Parsed at noon, not midnight, same reasoning as MetOnField.jsx's own
 * dateToIso: start_date/end_date are plain YYYY-MM-DD calendar dates with
 * no real time-of-day of their own, and midnight in the viewer's own local
 * time zone (this constructs a browser-local Date, not a UTC one) can be
 * the previous evening in a zone west of UTC, which would silently shift
 * the calendar day back by one for a viewer far enough west, San Antonio
 * or Grapevine both real examples. Noon is nowhere near a day boundary in
 * any real-world offset.
 */
export function formatShowDateRange(show) {
  if (!show?.start_date && !show?.end_date) return null;
  const start = show.start_date ? format(new Date(`${show.start_date}T12:00:00`), 'EEE MMM d') : null;
  const end = show.end_date ? format(new Date(`${show.end_date}T12:00:00`), 'EEE MMM d, yyyy') : null;
  if (start && end) return `${start} to ${end}`;
  return start || end;
}
